import { REGEX_ISO8601_NO_TZ, REGEX_ISO8601_DATEFORMAT } from '../validation/date';
import { anyOf } from '../js';
import { DateFormatters } from 'app/infrastructure/data/types';
import { DateFormat } from 'ui/common/types';

/**
 * Removes the 'Z' UTC indicator from a date, since our API cannot handle it
 * 
 * >Note: This creates a closure around the provided date, so it must 
 * be assigned at a point in time when it cannot be mutated anymore.
 * Assigning it as .toJSON when added to the immutable store you ensure that JSON.stringify
 * will use the current value. 
 * 
 * Monkeypatching is not an option, as this codebase might be using different APIs,
 * where some might handle dates differently
 */
export const toISODatewithoutTimezone = (date: Date) => () => {
  return !!date && (date instanceof Date) ? date.toISOString().replace('Z', '') : '';
};

/**
 * Clones a local date into a UTC Date - with GMT indicator in the string output.
 * The returned date will have year, month, day, hours and minutes assigned
 * from parameter `date` LOCAL time
 */
const cloneAsUTC = (date: Date) =>  (new Date(Date.UTC(
  date.getFullYear(), 
  date.getMonth(), 
  date.getDate(),
  date.getHours(),
  date.getMinutes(),
  date.getSeconds(),
  date.getMilliseconds()
)));

function dateFromISO8601(isoDateString: string) {
    const [ 
      year, 
      month, 
      day, 
      hours = 0, 
      minutes = 0, 
      seconds = 0, 
      milliseconds = 0 ] = isoDateString.match(/\d+/g).map((s) => Number(s));            

    return createUTCDate({
      year,
      // parsed calendar dates are +1 compared to how JavaScript count months
      month: month - 1,
      day,
      hours,
      minutes,
      seconds,
      milliseconds
    });
}

/**
 * Converts a timezone or non-timezone aware ISO8601 date string to a Date object.
 * 
 * For legacy resons we must completely ignore timezones as suffix when persisting
 */
export function parseAsDateUTC(dateString: string) : Date {

  if (typeof dateString !== 'string'){
    return undefined;
  }

  if ( REGEX_ISO8601_DATEFORMAT.test(dateString.toUpperCase())
        || REGEX_ISO8601_NO_TZ.test(dateString.toUpperCase()) ){

    return dateFromISO8601(dateString);
  }

  return undefined;
}

export const applyDateCalculation = (rawInput: string, now: Date) => {
  /**
   * Matches: "d+1", "m-22", "y+1" ....
   * > Case insensitive
   */
  const DMY_WITH_SIGN_AND_COUNT = /^(d|m|y)([+-]){1}([0-9]){0,4}$/i;  
  /**
   * Matches: "d", "m", "y"
   * > Case insensitive
   */
  const DMY_ONLY = /^(d|m|y){1}$/i;
  
  if(typeof rawInput !== 'string' || !now){
    return undefined;
  }

  const input = rawInput.trim();
  const [, 
    shortcut = undefined, 
    sign = undefined, 
    count = undefined
  ] = input.match(DMY_WITH_SIGN_AND_COUNT) || input.match(DMY_ONLY) || [];
  
  if(!shortcut){
    return undefined;
  }

  if(sign && count){
    let num = Number(count);
    if(sign === '-' && num > 0) { num = num*-1; }

    (shortcut === 'd') && now.setUTCDate(now.getUTCDate() + num);
    (shortcut === 'm') && now.setUTCMonth(now.getUTCMonth() + num);
    (shortcut === 'y') && now.setUTCFullYear(now.getUTCFullYear() + num);
  }

  return now;
};

/**
 * Parses a ISO8601 date string to a Date object.
 * Can also parse a date math expression such as `d+1` where Date.now() is used used as 
 * value for `d` and any additions are made to the UTC values of the Date object returned
 * 
 * @param expression {string} one of `d`, `m` or `y` modifiers. Optionally a addition or subtraction can be added in the
 * form of `+<n>` or `-<n>` where n is a integer. `d+1`, `d-10`, `m+3`, `y-1` would all be valid. The operator sign is **required**
 * and the expression is invalid if omitted. An expression with the length of 2 is always invalid.
 * @param date {Date} Optional. Defaults to Date.now instanciated as UTC/GMT date.
 */
export const parseDate = (expression, date = cloneAsUTC(new Date())) => anyOf([
   applyDateCalculation, 
   parseAsDateUTC
  ], expression, date);

/**
 * When 3rd party code creates new Date objects, this function will ensure
 * it's handled consistently troughout it's client lifetime.
 */
export function dateToUTC(date: Date){
  return cloneAsUTC(date);
}

/**
 * Creates a date as UTC intead of local time.
 * 
 * All code must create new Dates using this function, to
 * ensure we have control if future changes to database 
 * handling of dates are changed.
 * @param dateValues Passed in values will be set to the UTC time and date values. If not defined, Date.now() is used as UTC time and date.
 */
export function createUTCDate(dateValues: {
  year?: number, 
  month?: number, 
  day?: number, 
  hours?: number, 
  minutes?: number,
  seconds?: number,
  milliseconds?: number
} = {}){

  if (dateValues.year && dateValues.year < 1900){
    throw new Error('Use four digit year');
  }
  if (dateValues.month > 11 || dateValues.month < 0){
    throw new Error('Invalid month. Use JavaScript month indexer, 0-11');
  }
  const now = new Date();
  const calendarValues = {
    year: now.getFullYear(),
    month: now.getMonth(),
    day: now.getDate(),
    hours: now.getHours(),
    minutes: now.getMinutes(),
    seconds: now.getSeconds(),
    milliseconds: now.getMilliseconds(),
    ...(dateValues)
  };  
  return cloneAsUTC(new Date(
      calendarValues.year, 
      calendarValues.month, 
      calendarValues.day, 
      calendarValues.hours, 
      calendarValues.minutes, 
      calendarValues.seconds, 
      calendarValues.milliseconds
  ));
}

/**
 * Format a Date as string with UTC time
 * 
 * @param locale Intl locale string
 * @param dt The date to format. If undefined, an empty string will be returned
 * @param intlOptions Any overriding options merged onto the default {timeZone: 'UTC'} option object
 */
export function formatDateAsUTC( locale: string, dt: Date, intlOptions?: Intl.DateTimeFormatOptions){
    return (dt instanceof Date) ? new Intl.DateTimeFormat(locale, {timeZone: 'UTC', ...intlOptions}).format(dt) : '';
}

export const DEFAULT_DATE_FORMAT: Intl.DateTimeFormatOptions = { 
  weekday: `${DateFormat.Long}`,
  year: `${DateFormat.Numeric}`,
  month: `${DateFormat.Long}`,
  day: `${DateFormat.Numeric}`
};
export const DEFAULT_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  hour: `${DateFormat.Numeric}`,
  minute: `${DateFormat.Numeric}`
};

/**
 * Create formatters for Date, Time and Numbers, as a partial application based on passed in locale and existing intl.
 * TODO Needs more work to not be coupled to react-intl and component props assumptions
 * @param props 
 */
export const createFormatters = (
  locale: string
): DateFormatters => {

  return {
    formatDate: ( dt: Date, intlOptions: Intl.DateTimeFormatOptions = DEFAULT_DATE_FORMAT) => formatDateAsUTC(locale, dt, intlOptions),
    formatTime: ( dt: Date, intlOptions: Intl.DateTimeFormatOptions = DEFAULT_TIME_FORMAT) => formatDateAsUTC(locale, dt, intlOptions),
    // TODO: we really don't want number stuff here
    formatNumber: (n: number, intlOptions: Intl.NumberFormatOptions ) => new Intl.NumberFormat(locale, intlOptions).format(n)
  }; 
};

export const isDisabled = (disabled: Date) => !!disabled && disabled.getTime() <= new Date().getTime();
